import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
  Agent,
  LlmModel,
  ProjectPlan,
  ServiceAccount,
  serviceAccountStatus,
  ToolConnection,
  ToolInfo,
  toolsErrorMessage,
} from '../../models/ai-agents.models';
import { AiAgentsApiService } from '../../services/ai-agents-api.service';

/** Why a service is not available to the agent (contract v1.5, sections 2 and 3). */
export function unavailableReason(c: ToolConnection, sa: ServiceAccount | null): string {
  if (c.available) return '';
  if (!sa || !sa.connected) return 'No AI employee is connected to this agent.';
  if (sa.valid === false) return 'The AI employee\'s key is refused by Auth (switched off, new key or deleted).';
  return 'The service is not enabled in this project, or the AI employee\'s role has no access to it.';
}

/** The tools the agent may call: allowed ones, of an enabled and available connection. */
export function allowedToolCount(c: ToolConnection): number {
  return (c.tools || []).filter((t) => t.allowed).length;
}

/** "Reads" / "Changes data" / "Changes data (destructive)". */
export function toolKindLabel(t: Pick<ToolInfo, 'readOnly' | 'destructive'>): string {
  if (t.readOnly) return 'Reads';
  return t.destructive ? 'Changes data (destructive)' : 'Changes data';
}

/**
 * Contract v1.5, support view (system scope, read-only): the agent's AI employee and its MCP tool connections.
 * The customer connects the AI employee and switches services and tools on in the agents Dashboard; nothing is
 * changed here.
 */
@Component({
  selector: 'app-ai-agent-tools',
  templateUrl: './agent-tools.component.html',
  styleUrls: ['../../ai-agents.shared.css', './agent-tools.component.css'],
})
export class AgentToolsComponent implements OnChanges {
  @Input() agent!: Agent;
  /** The plan of the agent's project, for the notes only; null = unknown. */
  @Input() plan: ProjectPlan | null = null;
  /** The agent's model, for the "cannot use tools" note; undefined = unknown. */
  @Input() model: LlmModel | undefined;

  readonly unavailableReason = unavailableReason;
  readonly allowedToolCount = allowedToolCount;
  readonly toolKindLabel = toolKindLabel;

  account: ServiceAccount | null = null;
  accountLoading = false;
  accountError = '';

  connections: ToolConnection[] = [];
  toolsLoading = false;
  toolsError = '';

  constructor(private api: AiAgentsApiService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['agent'] && this.agent) {
      this.load();
    }
  }

  get status(): { label: string; css: string } {
    return serviceAccountStatus(this.account);
  }

  get planOff(): boolean {
    return !!this.plan && this.plan.allowTools === false;
  }

  get planMax(): number | null {
    return this.plan && typeof this.plan.maxToolConnectionsPerAgent === 'number' ? this.plan.maxToolConnectionsPerAgent : null;
  }

  get modelWithoutTools(): boolean {
    return !!this.model && this.model.supportsTools === false;
  }

  /** Switched-on connections (what the plan limit counts). */
  get enabledCount(): number {
    return this.connections.filter((c) => c.enabled).length;
  }

  /** The agent really calls MCP tools in a turn (contract v1.5, section 4); false when anything is missing. */
  get toolsInUse(): boolean {
    return (
      !this.planOff &&
      !this.modelWithoutTools &&
      !!this.account?.connected &&
      this.account.valid !== false &&
      this.connections.some((c) => c.enabled && c.available && allowedToolCount(c) > 0)
    );
  }

  load(): void {
    const agentId = this.agent.id;
    this.accountLoading = true;
    this.accountError = '';
    this.api.getAgentServiceAccount(agentId).subscribe({
      next: (sa) => {
        if (this.agent?.id !== agentId) return;
        this.account = sa || null;
        this.accountLoading = false;
      },
      error: (err) => {
        if (this.agent?.id !== agentId) return;
        this.account = null;
        this.accountLoading = false;
        this.accountError = toolsErrorMessage(err, 'Loading the AI employee failed.');
      },
    });
    this.toolsLoading = true;
    this.toolsError = '';
    this.api.listAgentTools(agentId).subscribe({
      next: (list) => {
        if (this.agent?.id !== agentId) return;
        this.connections = Array.isArray(list) ? list.map((c) => ({ ...c, tools: Array.isArray(c.tools) ? c.tools : [] })) : [];
        this.toolsLoading = false;
      },
      error: (err) => {
        if (this.agent?.id !== agentId) return;
        this.connections = [];
        this.toolsLoading = false;
        this.toolsError = toolsErrorMessage(err, 'Loading the tool connections failed.');
      },
    });
  }
}
